import { useEffect, useRef, useState } from 'react';

import { STYLE_BLOB_TYPE } from './constants';
import type { UseResolvedStyle } from './types';

type StyleJson = {
    sprite?: string;
    [key: string]: unknown;
};

const isRootRelative = (url: string): boolean => url.startsWith('/');

const toAbsolute = (url: string): string => `${window.location.origin}${url}`;

const patchStyle = (style: StyleJson): StyleJson => {
    if (style.sprite && isRootRelative(style.sprite)) {
        return { ...style, sprite: toAbsolute(style.sprite) };
    }

    return style;
};

const useResolvedStyle: UseResolvedStyle = path => {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const cacheRef = useRef(new Map<string, string>());

    useEffect(() => {
        const cache = cacheRef.current;
        const cached = cache.get(path);

        if (cached) {
            setBlobUrl(cached);

            return;
        }

        // Clear stale URL so Map waits for the new blob instead of mounting with the
        // previous variant's URL.
        setBlobUrl(null);

        let cancelled = false;

        const load = async () => {
            const response = await fetch(path);
            const style = (await response.json()) as StyleJson;
            const patched = patchStyle(style);
            const blob = new Blob([JSON.stringify(patched)], { type: STYLE_BLOB_TYPE });
            const url = URL.createObjectURL(blob);

            if (cancelled) {
                URL.revokeObjectURL(url);

                return;
            }

            cache.set(path, url);
            setBlobUrl(url);
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [path]);

    useEffect(
        () => () => {
            cacheRef.current.forEach(url => URL.revokeObjectURL(url));
            cacheRef.current.clear();
        },
        []
    );

    return blobUrl;
};

export default useResolvedStyle;
